import React, { useState, useMemo, useEffect, useRef } from "react";
import { getCookie } from "../helpers/index";
import { InputLabel, TextField, InputAdornment } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import "./components.css";
import CustomTooltip from "./CustomTooltip";
import { useTranslation } from "react-i18next";

const containsText = (text, searchText) =>
  text.toLowerCase().indexOf(searchText.toLowerCase()) > -1;

export default function DropdownComponent(props) {
  const { label, value, onChange, required, enumValues, name, tooltip, disabled, pageKey } = props;
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const pageIndex = getCookie("pageIndex");
  const dropdownRef = useRef(null);
  const dropdownMenuRef = useRef(null);

  const displayedOptions = useMemo(
    () => enumValues?.filter((option) => containsText(option, searchText)),
    [searchText, enumValues]
  );

  const handleOnChange = (option) => {
    if (disabled) return;
    let val = option === "--Select--" ? "" : option;
    onChange({
      target: {
        name,
        value: val,
      },
    });
    setIsOpen(false);
  };

  const tooltipMsg = () => (tooltip ? tooltip : "");

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsOpen(false);
    }
  };
  useEffect(() => {
    // Function to adjust margin based on the dropdown state
    const adjustRightContentDivMargin = (isOpen) => {
      const rightContentDiv = document.querySelector(".righContentDiv");
      if (rightContentDiv) {
        // Set margin based on mobile or desktop view
        rightContentDiv.style.marginBottom = isOpen 
          ? (window.innerWidth < 600 ? pageKey === "LoanDetails" ? "240px" : "120px" : pageKey === "LoanDetails" ? "240px" : "150px") 
          : (window.innerWidth < 600 ? "50px" : "64px");
      }
    };
  
    if (isOpen) {
      const windowHeight = window.innerHeight;
      const dropdownMenu = dropdownMenuRef.current;
  
      if (dropdownMenu) {
        const { bottom } = dropdownMenu.getBoundingClientRect();
  
        // Adjust margin when dropdown is open
        adjustRightContentDivMargin(true);
  
        // Check if the dropdown is near the bottom of the viewport
        if (bottom > windowHeight) {
          // Scroll down by 200px
          window.scrollBy({ top: 200, behavior: "smooth" });
        }
      }
    } else {
      // Adjust margin when dropdown is closed
      adjustRightContentDivMargin(false);
    }
  }, [isOpen]);
  
  useEffect(() => {
    // Set the initial margin on mount
    const rightContentDiv = document.querySelector(".righContentDiv");
    if (rightContentDiv) {
      rightContentDiv.style.marginBottom = window.innerWidth < 600 ? "50px" : "64px";
    }
  
    // Add event listener to handle clicks outside the dropdown
    window.addEventListener("mousedown", handleClickOutside);
  
    return () => {
      // Clean up the event listener on component unmount
      window.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);
  
  return (
    <div className={`${pageKey === "declarations" ? "declarationsDropdown" : "flexAlignColumnClassName"}`} ref={dropdownRef}>
      <InputLabel
        id="search-select-label"
        required={required}
        className="labelClassName alignRowClassName flex-start"
      >
        {t(label)}
        {tooltipMsg() && (
          <CustomTooltip title={tooltipMsg()}>
            <span className="tooltipInfoIcon">i</span>
          </CustomTooltip>
        )}
      </InputLabel>
      <div className={`${pageIndex === "8" ? "dropDownComponentFCWd" : "dropDownComponentFCWdOne"} dropdownHeader`}>
        <div
          className="textFieldInputComponent dropdown"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
        >
          <span className={`${disabled ? "disabled-dropdown" : (pageKey === "declarations" ? "declarations-dropdown-selected" : "dropdown-selected")}`}>
            {value ? t(value) : t("--Select--")}
          </span>
          <span className="dropdown-icon">
            <ArrowDropDownIcon
              style={{
                transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.3s',
                color: disabled ? 'rgba(0, 0, 0, 0.38)' : ''
              }} />
          </span>
        </div>
        {isOpen && !disabled && (
          <div
            className="dropdownMenu"
            style={{ maxHeight: '200px', overflowY: 'auto'}}
            ref={dropdownMenuRef}
          >
            <TextField
              size="small"
              data-testid="textfield"
              placeholder={t("Type to search...")}
              autoFocus={false}
              fullWidth
              value={searchText}
              className="textFieldInputComponent dropdownSearch"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
                classes: {
                  root: "outlinedInputRoot",
                  focused: "outlinedInputFocused",
                  notchedOutline: "outlinedInput",
                  input: "checkComponi",
                },
              }}
              onChange={(e) => setSearchText(e.target.value)}
              onKeyDown={(e) => {
                if (e.key !== "Escape") {
                  e.stopPropagation();
                }
              }}
            />
            <div className="dropdownOptions">
              <div
                className={`dropdownOptionTag ${value === "" ? "selectedOption" : ""}`}
                onClick={() => handleOnChange("--Select--")}
              >
                {t("-- Select --")}
              </div>
              {displayedOptions?.map((option, i) => (
                <div
                  key={i}
                  className={`dropdownOptionTag ${value === option ? "selectedOption" : ""} checkComponi`}
                  onClick={() => handleOnChange(option)}
                >
                  {t(option)}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
